import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { CalendarDays, RefreshCw, Video } from 'lucide-react';

function getMeetingStatusBadge(status) {
  const styles = {
    scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
    completed: 'bg-green-100 text-green-800 border-green-200',
    cancelled: 'bg-red-100 text-red-800 border-red-200',
    reschedule_requested: 'bg-yellow-100 text-yellow-800 border-yellow-200'
  };
  return <Badge variant="outline" className={styles[status] || styles.scheduled}>{status?.replace('_', ' ')}</Badge>;
}

export function ClientMeetingsTab({
  meetings, availableSlots, onBookSlot,
  rescheduleDialogOpen, setRescheduleDialogOpen,
  rescheduleReason, setRescheduleReason,
  onRequestReschedule, onOpenReschedule
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-primary" />
            Your Meetings
          </CardTitle>
        </CardHeader>
        <CardContent>
          {meetings.length === 0 && availableSlots.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDays className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No meetings scheduled</p>
            </div>
          ) : (
            <div className="space-y-4">
              {availableSlots.map((slot) => (
                <div key={slot.id} className="p-4 border-2 border-primary/20 rounded-lg bg-primary/5">
                  <h4 className="font-semibold mb-2">Book: {slot.title}</h4>
                  <div className="flex flex-wrap gap-2">
                    {slot.slots?.map((s, idx) => (
                      <Button key={idx} variant="outline" size="sm" onClick={() => onBookSlot(slot.id, idx)}>
                        {s.date} {s.start_time}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
              {meetings.map((m) => (
                <div key={m.id} className="p-4 border rounded-lg" data-testid={`client-meeting-${m.id}`}>
                  <div className="flex justify-between mb-2">
                    <h4 className="font-semibold">{m.title}</h4>
                    {getMeetingStatusBadge(m.status)}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {new Date(m.start_time).toLocaleString()} {m.is_virtual ? '(Virtual)' : '(In-person)'}
                  </p>
                  {m.video_platform && m.video_platform !== 'none' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {m.video_platform === 'zoom' ? 'Zoom' : 'Video Call'}
                    </p>
                  )}
                  <div className="flex gap-2 mt-2">
                    {m.video_link && m.is_virtual && m.status === 'scheduled' && (
                      <a href={m.video_link} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`join-meeting-btn-${m.id}`}>
                          <Video className="w-4 h-4 mr-1" />Join Meeting
                        </Button>
                      </a>
                    )}
                    {!m.video_link && m.location && m.is_virtual && m.status === 'scheduled' && (
                      <a href={m.location} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`join-meeting-btn-${m.id}`}>
                          <Video className="w-4 h-4 mr-1" />Join Meeting
                        </Button>
                      </a>
                    )}
                    {!m.video_link && !m.location && m.video_platform && m.video_platform !== 'none' && m.status === 'scheduled' && (
                      <Button size="sm" variant="outline" disabled className="text-muted-foreground" data-testid={`meeting-link-pending-${m.id}`}>
                        <Video className="w-4 h-4 mr-1" />Link pending - contact your case manager
                      </Button>
                    )}
                    {m.status === 'scheduled' && (
                      <Button variant="outline" size="sm" onClick={() => onOpenReschedule(m.id)}>
                        <RefreshCw className="w-4 h-4 mr-1" />Reschedule
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request Reschedule</DialogTitle></DialogHeader>
          <Textarea value={rescheduleReason} onChange={(e) => setRescheduleReason(e.target.value)} placeholder="Reason (optional)" />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)}>Cancel</Button>
            <Button onClick={onRequestReschedule}>Submit</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
